/**
 * Lactate API - Wrapper for lactate monitor functionality
 */

import { nativeBridge } from "../index"
import { ErrorCode } from "../types"
import type {
  LactateConnectParams,
  LactateConnectResponse,
  LactateMeasurement,
  LactateGetMeasurementsResponse,
  LactateConnectedEvent,
  LactateDisconnectedEvent,
  LactateMeasurementEvent,
  LactateErrorEvent,
  LactateAutoSyncStatusResponse,
  LactateConnectionStatusResponse,
  LactateListDevicesResponse,
  LactatePairedDevice,
  LactateActiveDeviceChangedEvent,
  LactateDeviceForgottenEvent,
  LactateDevicePairedEvent,
} from "../types"
import type { RetryOptions } from "../utils"

export class LactateAPI {
  private connectionSubscriptionId: string | null = null
  private measurementSubscriptionId: string | null = null

  /**
   * Connect to lactate monitor device
   */
  async connect(timeout?: number): Promise<LactateConnectResponse> {
    // Retry connection up to 3 times with exponential backoff
    const retryOptions: RetryOptions = {
      maxAttempts: 3,
      delay: 2000,
      backoff: true,
      shouldRetry: (error) => {
        // Retry on device not found and connection failed
        return [ErrorCode.DEVICE_NOT_FOUND, ErrorCode.CONNECTION_FAILED, ErrorCode.TIMEOUT].includes(
          error.code as ErrorCode,
        )
      },
    }

    return nativeBridge.request<LactateConnectParams, LactateConnectResponse>(
      "lactate.connect",
      { timeout },
      timeout || 35000, // Use 35 seconds to be longer than device scan timeout
      retryOptions,
    )
  }

  /**
   * Disconnect from lactate monitor device
   */
  async disconnect(): Promise<void> {
    return nativeBridge.request("lactate.disconnect")
  }

  /**
   * Get all lactate measurements
   */
  async getMeasurements(): Promise<LactateGetMeasurementsResponse> {
    return nativeBridge.request<void, LactateGetMeasurementsResponse>("lactate.getMeasurements")
  }

  /**
   * Clear all stored measurements
   */
  async clearMeasurements(): Promise<void> {
    return nativeBridge.request("lactate.clearMeasurements")
  }

  /**
   * Subscribe to connection events
   */
  async onConnectionChange(
    callback: (connected: boolean, data?: LactateConnectedEvent | LactateDisconnectedEvent) => void,
  ): Promise<() => Promise<void>> {
    // Subscribe to connected event
    const connectedId = await nativeBridge.subscribe<LactateConnectedEvent>("lactate.connected", (data) =>
      callback(true, data),
    )

    // Subscribe to disconnected event
    const disconnectedId = await nativeBridge.subscribe<LactateDisconnectedEvent>("lactate.disconnected", (data) =>
      callback(false, data),
    )

    // Return unsubscribe function
    return async () => {
      await nativeBridge.unsubscribe(connectedId)
      await nativeBridge.unsubscribe(disconnectedId)
    }
  }

  /**
   * Subscribe to new measurements
   */
  async onMeasurement(callback: (measurement: LactateMeasurement) => void): Promise<() => Promise<void>> {
    const subscriptionId = await nativeBridge.subscribe<LactateMeasurementEvent>("lactate.measurement", (event) =>
      callback(event.measurement),
    )

    // Return unsubscribe function
    return async () => {
      await nativeBridge.unsubscribe(subscriptionId)
    }
  }

  /**
   * Subscribe to errors
   */
  async onError(callback: (error: LactateErrorEvent) => void): Promise<() => Promise<void>> {
    const subscriptionId = await nativeBridge.subscribe<LactateErrorEvent>("lactate.error", callback)

    // Return unsubscribe function
    return async () => {
      await nativeBridge.unsubscribe(subscriptionId)
    }
  }

  /**
   * Get connection status
   */
  async getConnectionStatus(): Promise<LactateConnectionStatusResponse> {
    return nativeBridge.request<void, LactateConnectionStatusResponse>("lactate.getState", undefined, 5000)
  }

  /**
   * Get auto-sync status
   */
  async getAutoSyncStatus(): Promise<LactateAutoSyncStatusResponse> {
    return nativeBridge.request<void, LactateAutoSyncStatusResponse>("lactate.getAutoSyncStatus", undefined, 5000)
  }

  /**
   * Toggle auto-sync
   */
  async toggleAutoSync(enabled: boolean): Promise<void> {
    await nativeBridge.request<{ enabled: boolean }, void>("lactate.toggleAutoSync", { enabled }, 5000)
  }

  // --- Multi-device (lactateV2) -------------------------------------------

  /**
   * List all paired lactometers and which one is active. Requires the
   * `lactateV2` capability; older native builds don't implement it.
   */
  async listDevices(): Promise<LactateListDevicesResponse> {
    return nativeBridge.request<void, LactateListDevicesResponse>("lactate.listDevices", undefined, 5000)
  }

  /**
   * The user's registered meters that are ACTUALLY advertising right now (scans
   * briefly, ~up to 7.5s). The workout import uses this to decide: 0 present →
   * offline, 1 → read it directly (no picker), >1 → let the user choose. Requires
   * the `lactateV2` capability.
   */
  async findAvailableDevices(): Promise<{ devices: LactatePairedDevice[] }> {
    return nativeBridge.request<void, { devices: LactatePairedDevice[] }>(
      "lactate.findAvailableDevices",
      undefined,
      10000, // longer than the native scan window
    )
  }

  /**
   * Make a paired device the active one (the device the UI acts on).
   */
  async setActiveDevice(address: string): Promise<{ activeAddress: string }> {
    return nativeBridge.request<{ address: string }, { activeAddress: string }>(
      "lactate.setActiveDevice",
      { address },
      5000,
    )
  }

  /**
   * Connect to a specific paired device (drops any current BLE link first).
   * Used when the user opens a device to work with it live.
   */
  async connectDevice(address: string): Promise<LactateConnectResponse> {
    return nativeBridge.request<{ address: string }, LactateConnectResponse>(
      "lactate.connectDevice",
      { address },
      35000,
    )
  }

  /**
   * Cached measurements for one device (no connection required).
   */
  async getDeviceMeasurements(address: string): Promise<LactateGetMeasurementsResponse> {
    return nativeBridge.request<{ address: string }, LactateGetMeasurementsResponse>(
      "lactate.getDeviceMeasurements",
      { address },
      5000,
    )
  }

  /**
   * Add ANOTHER lactometer via the system picker, even when one is already
   * paired (unlike `connect`, which reuses the existing pairing).
   */
  async addDevice(timeout?: number): Promise<LactateConnectResponse> {
    return nativeBridge.request<LactateConnectParams, LactateConnectResponse>(
      "lactate.addDevice",
      { timeout },
      timeout || 35000,
    )
  }

  /**
   * Forget a paired device (drops the pairing, tears down the OS association,
   * disconnects it if it's the live one).
   */
  async forgetDevice(address: string): Promise<void> {
    await nativeBridge.request<{ address: string }, void>("lactate.forgetDevice", { address }, 5000)
  }

  /**
   * Mute/unmute background discovery-sync for a single device without
   * forgetting it.
   */
  async setDeviceSyncOnDiscovery(address: string, enabled: boolean): Promise<void> {
    await nativeBridge.request<{ address: string; enabled: boolean }, void>(
      "lactate.setDeviceSyncOnDiscovery",
      { address, enabled },
      5000,
    )
  }

  // --- EN605 device-configuration writes (lactateV2) ----------------------

  /**
   * Set the active EN605's clock. Defaults to "now" when `timeMs` is omitted.
   */
  async setDeviceTime(timeMs?: number): Promise<void> {
    await nativeBridge.request<{ timeMs?: number }, void>(
      "lactate.setDeviceTime",
      timeMs != null ? { timeMs } : {},
      10000,
    )
  }

  /**
   * Erase records stored on the active EN605.
   */
  async clearDevice(all = false): Promise<void> {
    await nativeBridge.request<{ all: boolean }, void>("lactate.clearDevice", { all }, 10000)
  }

  /**
   * Subscribe to registry changes: a device becoming active, being forgotten,
   * or a new one being paired. The callback fires on any of the three; use it
   * to re-fetch `listDevices`.
   */
  async onDevicesChanged(
    callback: (
      change:
        | { type: "activeChanged"; data: LactateActiveDeviceChangedEvent }
        | { type: "forgotten"; data: LactateDeviceForgottenEvent }
        | { type: "paired"; data: LactateDevicePairedEvent },
    ) => void,
  ): Promise<() => Promise<void>> {
    const activeId = await nativeBridge.subscribe<LactateActiveDeviceChangedEvent>(
      "lactate.activeDeviceChanged",
      (data) => callback({ type: "activeChanged", data }),
    )
    const forgottenId = await nativeBridge.subscribe<LactateDeviceForgottenEvent>("lactate.deviceForgotten", (data) =>
      callback({ type: "forgotten", data }),
    )
    const pairedId = await nativeBridge.subscribe<LactateDevicePairedEvent>("lactate.devicePaired", (data) =>
      callback({ type: "paired", data }),
    )

    return async () => {
      await nativeBridge.unsubscribe(activeId)
      await nativeBridge.unsubscribe(forgottenId)
      await nativeBridge.unsubscribe(pairedId)
    }
  }

  /**
   * Subscribe to any event by name
   */
  async onEvent<T = unknown>(eventName: string, callback: (data: T) => void): Promise<() => Promise<void>> {
    const subscriptionId = await nativeBridge.subscribe<T>(eventName, callback)

    // Return unsubscribe function
    return async () => {
      await nativeBridge.unsubscribe(subscriptionId)
    }
  }
}

// Export singleton instance
export const lactateAPI = new LactateAPI()
